import { useState } from "react";
import { ExternalLink, Cloud } from "lucide-react";
import PortalLinkDialog from "@/components/module_portal_wizard/PortalLinkDialog";

/**
 * GoogleBilderHinweis (A-026, 24.10.2026) — Gmail legt große Anhänge bei Google Drive ab; in der Mail steht nur ein Link,
 * die Dateien sind für niemanden außer dem Absender freigegeben. Die Suite kann sie nicht laden.
 * Gemeinsamer Baustein für Mail-Zentrale (MailAnhaengeBereich) und Mailverlauf (MailHistoryModal).
 * Props: { links: [{id,name,url,art}], kunde: {id,name,email,...}|null, projekt: {id,titel}|null }
 * „Kundin um die Bilder bitten“ öffnet den einheitlichen Portal-Link-Dialog (PortalLinkDialog).
 */
const GoogleBilderHinweis = ({ links = [], kunde = null, projekt = null }) => {
  const [offen, setOffen] = useState(false);
  if (!links.length) return null;
  const n = links.length;
  const nurBilder = links.every((l) => l.art !== "datei");
  const wort = nurBilder ? (n === 1 ? "Bild" : "Bilder") : (n === 1 ? "Datei" : "Dateien");
  const satz = n === 1 ? `1 ${wort} liegt bei Google und ist nicht freigegeben` : `${n} ${wort} liegen bei Google und sind nicht freigegeben`;
  const kundeOk = !!kunde?.id;

  return (
    <div className="border border-amber-300 bg-amber-50 text-amber-950 dark:bg-amber-950/30 dark:text-amber-100 dark:border-amber-700 rounded-sm p-2 space-y-2 text-xs" data-testid="google-bilder">
      <div className="flex items-start gap-1.5">
        <Cloud className="w-4 h-4 shrink-0 mt-0.5" />
        <div>
          <div className="font-semibold" data-testid="google-bilder-hinweis">{satz}</div>
          <div className="text-amber-900/80 dark:text-amber-200/80">
            Gmail hat sie wegen der Größe nicht angehängt. Die Suite kann sie nicht laden – bitten Sie die Kundin, sie über das Kundenportal hochzuladen.
          </div>
        </div>
      </div>
      <ul className="space-y-1" data-testid="google-bilder-liste">
        {links.map((l) => (
          <li key={l.id} className="flex flex-wrap items-center justify-between gap-x-2 gap-y-1 border border-amber-200 dark:border-amber-800 bg-background text-foreground rounded-sm px-2 py-1">
            <span className="truncate min-w-0" title={l.name}>{l.name}</span>
            <a href={l.url} target="_blank" rel="noopener noreferrer"
              className="inline-flex items-center gap-1 text-primary hover:underline shrink-0" data-testid="google-bild-oeffnen">
              <ExternalLink className="w-3 h-3" /> Bei Google öffnen
            </a>
          </li>
        ))}
      </ul>
      <div className="flex flex-wrap items-center gap-2">
        <button type="button" disabled={!kundeOk} onClick={() => setOffen(true)}
          title={kundeOk ? "Portal-Link an die Kundin schicken, damit sie die Bilder hochlädt" : "Erst Kunde anlegen"}
          className="px-3 py-1.5 text-xs bg-emerald-600 text-white rounded-sm hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed"
          data-testid="btn-google-bilder-bitten">
          Kundin um die Bilder bitten
        </button>
        {!kundeOk && <span className="text-amber-900 dark:text-amber-200 font-medium" data-testid="google-bilder-kein-kunde">Erst Kunde anlegen</span>}
      </div>
      {offen && kundeOk && <PortalLinkDialog kunde={kunde} projekt={projekt} vorschlag="bilder" onClose={() => setOffen(false)} />}
    </div>
  );
};

export default GoogleBilderHinweis;
